import { useState } from 'react'
import { useEmployment, useCreateEmployment, useUpdateEmployment, useDeleteEmployment } from '@/hooks/useEmployment'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { EmptyState } from '@/components/profile/EmptyState'
import { Briefcase, Plus, Pencil, Trash2 } from 'lucide-react'
import { formatDate } from '@/lib/utils'

const INCOME_RANGES = ['< 10k', '10k-25k', '25k-50k', '50k-100k', '> 100k', 'prefer_not_to_say']

type FormData = { id?: string; company: string; position: string; start_date: string; end_date: string; isCurrent: boolean; monthly_income_range: string; notes: string }
const empty: FormData = { company: '', position: '', start_date: '', end_date: '', isCurrent: false, monthly_income_range: '', notes: '' }

export function EmploymentPage() {
  const { data: items = [], isLoading } = useEmployment()
  const create = useCreateEmployment()
  const updateEmp = useUpdateEmployment()
  const deleteEmp = useDeleteEmployment()
  const [form, setForm] = useState<FormData | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const openEdit = (item: Record<string, unknown>) => setForm({
    id: item.id as string, company: item.company as string, position: item.position as string,
    start_date: item.start_date as string, end_date: (item.end_date as string) ?? '',
    isCurrent: !item.end_date, monthly_income_range: (item.monthly_income_range as string) ?? '', notes: (item.notes as string) ?? '',
  })

  const handleSave = () => {
    if (!form || !form.company || !form.position || !form.start_date) { setFormError('Company, position, and start date are required'); return }
    if (!form.isCurrent && form.end_date && form.end_date < form.start_date) { setFormError('End date must be after start date'); return }
    setFormError(null)
    const payload = {
      company: form.company, position: form.position, start_date: form.start_date,
      end_date: form.isCurrent ? null : form.end_date || null,
      monthly_income_range: form.monthly_income_range || null, notes: form.notes || null,
    }
    if (form.id) {
      updateEmp.mutate({ id: form.id, ...payload }, { onSuccess: () => setForm(null) })
    } else {
      create.mutate(payload, { onSuccess: () => setForm(null) })
    }
  }

  if (isLoading) return <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-lg bg-muted" />)}</div>

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button size="sm" onClick={() => setForm({ ...empty })}><Plus className="h-3 w-3" /> Add Employment</Button></div>

      {items.length === 0 ? (
        <EmptyState icon={Briefcase} title="No employment entries yet" description="Add your work history." actionLabel="Add Employment" onAction={() => setForm({ ...empty })} />
      ) : items.map((item) => (
        <Card key={item.id}>
          <CardContent className="flex items-start justify-between pt-4">
            <div>
              <p className="font-medium text-sm">{item.position}</p>
              <p className="text-xs text-muted-foreground">{item.company}</p>
              <p className="text-xs text-muted-foreground">{formatDate(item.start_date)} – {item.end_date ? formatDate(item.end_date) : 'Present'}</p>
              {item.is_current && <span className="inline-block mt-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-800">Current</span>}
              {item.monthly_income_range && <span className="inline-block mt-1 ml-1 rounded-full bg-muted px-2 py-0.5 text-[10px]">{item.monthly_income_range}</span>}
            </div>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="icon" variant="ghost" onClick={() => deleteEmp.mutate(item.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setForm(null)}>
          <div className="w-full max-w-sm max-h-[90vh] overflow-y-auto rounded-lg bg-background p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-3 font-semibold">{form.id ? 'Edit' : 'Add'} Employment</h3>
            {formError && <p className="mb-2 text-xs text-red-600">{formError}</p>}
            <div className="space-y-3">
              <div className="space-y-1"><Label>Company *</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              <div className="space-y-1"><Label>Position *</Label><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} /></div>
              <div className="space-y-1"><Label>Start Date *</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isCurrent} onChange={(e) => setForm({ ...form, isCurrent: e.target.checked })} className="h-4 w-4 rounded border-input" /> I currently work here</label>
              {!form.isCurrent && <div className="space-y-1"><Label>End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>}
              <div className="space-y-1">
                <Label>Monthly Income Range</Label>
                <Select value={form.monthly_income_range} onValueChange={(v) => setForm({ ...form, monthly_income_range: v })}><SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger><SelectContent>{INCOME_RANGES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <Button className="w-full" onClick={handleSave} disabled={create.isPending || updateEmp.isPending}>Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
